import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { clone, getElementById } from "../util/Utils";
import type { Predicate } from "../types/Predicate";
import { extendNbaLabel, getStatus, updateLabel, updateStatus } from "../tracking/FeatureTracking";
import type { FeatureStatus } from "../tracking/Tracking";
import type { TrackingLabels } from "../tracking/TrackingLabels";

const FACET_VALUE_CLASS = `hc_facetValueInput`;
const FACET_VALUE_TITLE_CLASS = `hc_label__title`;
const FACET_VALUE_TITLE_SELECTOR = `.${FACET_VALUE_TITLE_CLASS}`;
const CLONED_NBA_ID_SUFFIX = `--nba--cloned`;

export class SvelteFacetValue {
  static readonly factory = HeurekaElementFactory.byElement("input", FACET_VALUE_CLASS, SvelteFacetValue);
  readonly label: HTMLLabelElement | null;
  readonly span: HTMLSpanElement | null;

  /*               */
  constructor(
    readonly facetValue: HTMLInputElement,
    readonly liElem: HTMLLIElement | null = facetValue.closest("li"),
  ) {
    this.label = facetValue.closest("label");
    this.span = liElem.querySelector(FACET_VALUE_TITLE_SELECTOR);
  }

  /*                  */
  /*               */
  /*                       */

  get id(): string | undefined {
    return this.facetValue.id;
  }

  get name(): string | undefined {
    return this.facetValue.name;
  }

  get checked(): boolean {
    return this.facetValue.checked;
  }

  get nba(): boolean {
    return this.facetValue.dataset.nba === "true";
  }

  get ref(): string | undefined {
    return this.facetValue.dataset.tsFeatureRef;
  }

  get referencedElement(): HTMLElement | undefined {
    return getElementById(this.ref);
  }

  set title(title: string | null | undefined) {
    if (this.span && title) {
      this.span.innerHTML = title;
    }
  }

  private clone(): SvelteFacetValue {
    return SvelteFacetValue.factory.pick(undefined, clone(this.liElem));
  }

  cloneWithData(id: string, title: string): SvelteFacetValue {
    const svelteFacetValue = this.clone();
    svelteFacetValue.facetValue.id = `${id}${CLONED_NBA_ID_SUFFIX}`;
    svelteFacetValue.facetValue.dataset.tsFeatureRef = id;
    svelteFacetValue.title = title;
    return svelteFacetValue;
  }

  /*                                                       */
  updateTrackingLabelsOnReferenced(status: FeatureStatus, filterMethod: TrackingLabels["san_FilterMethod"]) {
    if (this.referencedElement) {
      updateStatus(this.referencedElement, status, 2);
      updateLabel(this.facetValue, "san_FilterMethod", filterMethod);
    }
    return this;
  }
}

export const isSelected: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => value.checked;
export const isNba: Predicate<SvelteFacetValue> = (value: SvelteFacetValue) => value.nba;
export const updateFacetValueTagPosition = (value: SvelteFacetValue, index: number) =>
  value.referencedElement && updateLabel(value.referencedElement, "san_FacetValueTagPosition", (index + 1).toString());
export const updateNBAPosition = (value: SvelteFacetValue, index: number) =>
  value.referencedElement && updateLabel(value.referencedElement, "san_NBAPosition", (index + 1).toString());
export const updateSanTopFacetValue = (value: SvelteFacetValue) =>
  value.referencedElement && extendNbaLabel(value.referencedElement, "san_TopFacetValue", "nba");
export const setVisibleStatus = (value: SvelteFacetValue) => {
  const referencedElement = value.referencedElement;
  if (!referencedElement) return;

  const status = getStatus(referencedElement);
  if (!status || status === "loaded" || status === "hidden") {
    updateStatus(referencedElement, "visible");
  }
};
